/*
 * File: WeSpeakCommunityGuidelines.jsx
 * Project: our-wave-stories-app
 *
 * Created by Brendan Michaelsen on April 9, 2025 at 5:09 PM
 * Copyright © 2025 Our Wave, Inc. All rights reserved.
 *
 * Last Modified: May 31, 2025 at 10:28 PM
 * Modified By: Brendan Michaelsen
 */

/**
 * Imports
 */

// Modules
import React, { useEffect, useMemo } from 'react';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';

// Components
import {
	Meta,
	AppNavigation,
	LegalNavigation,
	Typography,
	LocaleLink,
} from '../../../../../src/components';
import { HighlightSection } from '../../../../../src/components/LegalNavigation/LegalNavigation.styles';

// Constants
import { AVAILABLE_I18N_NAMESPACES } from '../../../../../constants/base.constants';
import { LEGAL_NAVIGATION_SECTIONS } from '../../Constants';

// Styles
import * as S from './WeSpeakCommunityGuidelines.styles';


/**
 * Component
 */

const WeSpeakCommunityGuidelines = ({ meta: m }) => {

	// Get current partner from hook
	const partner = useSelector((state) => state.partner.value);

	// Get current locale from hook
	const locale = useSelector((state) => state.locale.value);

	// Get translation hook
	const [t] = useTranslation(AVAILABLE_I18N_NAMESPACES.CLIENT);

	// Define meta content
	const meta = useMemo(() => ({
		title: t('pageWeSpeakCommunityGuidelines.metaTitle', { partnerName: partner.name }),
		description: t('pageWeSpeakCommunityGuidelines.metaDescription', { partnerName: partner.name }),
		bodyClasses: 'hide-captcha',
		...m || {}
	}), [m, partner, t]);

	// Create navigation sections
	const navigationSections = useMemo(() => LEGAL_NAVIGATION_SECTIONS(locale), [locale]);

	// Handle actions on component load
	useEffect(() => {}, []);

	// Render component
	return (
		<>
			{/* Meta */}
			<Meta meta={meta} data={{}} />

			{/* Component Content */}
			<AppNavigation showFooter contentPadding={false}>
				<LegalNavigation
					navigationSections={navigationSections}
					mobileNavigationHeight={440}
				>
					<S.Wrapper>

						{/* Title */}
						<Typography tag="h1" weight="bold">
							We-Speak Community Guidelines
						</Typography>

						{/* TL;DR */}
						<HighlightSection>
							<Typography tag="p">
								The We-Speak platform is powered by
								{' '}
								<LocaleLink
									title="Our Wave"
									aria-label="Our Wave"
									target="_blank"
									to="https://www.ourwave.org"
									rel="noreferrer"
								>
									Our Wave
								</LocaleLink>
								, a nonprofit
								organization dedicated to building meaningful technology to empower survivors of sexual violence. For more
								information about our technology, team, or partnership opportunities, please
								{' '}
								<LocaleLink
									title="Visit Our Website"
									aria-label="Visit Our Website"
									target="_blank"
									to="https://www.ourwave.org"
									rel="noreferrer"
								>
									visit our website
								</LocaleLink>
								.
							</Typography>
						</HighlightSection>

						{/* Introduction */}
						<Typography tag="p">
							As a platform for healing and self-expression, We-Speak is deeply committed to supporting survivors of sexual
							violence and creating brave spaces for self-reflection, connection, and learning. At the same time, we draw
							lines around a few narrowly defined but deeply important categories of content and behaviour that jeopardize
							survivor wellbeing, threaten our infrastructure, and damage our healing community.
						</Typography>

						{/* Brave Space */}
						<br />
						<br />
						<Typography tag="p" fontStyle="italic">
							There is no such thing as a “safe space” —
							<br />
							We exist in the real world.
							<br />
							We all carry scars and have caused wounds.
							<br />
							This space
							<br />
							seeks to turn down the volume of the world outside,
							<br />
							and amplify voices that have to fight to be heard elsewhere,
							<br />
							This space will not be perfect.
							<br />
							It will not always be what we wish it to be
							<br />
							But
							<br />
							It will be our space together,
							<br />
							and we will work on it side by side.
							<br />
						</Typography>
						<Typography tag="p">
							— “Untitled Poem” by Beth Strano
						</Typography>

						{/* What We-Speak Is For */}
						<br />
						<br />
						<Typography tag="h2" weight="bold">
							What We-Speak Is For
						</Typography>
						<Typography tag="p">
							We-Speak celebrates sexual violence survivorship in all forms. We want you to express yourself freely and use We-Speak
							to reflect on your experiences, connect with other survivors, and think deeply about what healing means to you. We
							also want you to be mindful and respectful of differences in survivor experiences and variations in the healing journey.
						</Typography>

						{/* What We-Speak Is Not For */}
						<br />
						<Typography tag="h2" weight="bold">
							What We-Speak Is
							{' '}
							<span className="underline">Not</span>
							{' '}
							For
						</Typography>
						<ul className="list-padding">
							<li>
								<b>Hate Speech.</b>
								{' '}
								We
								<span className="underline">do not</span>
								{' '}
								tolerate content that actively blames survivors or
								encourages violence or hatred.
								{' '}
								<span className="underline">Do not</span>
								{' '}
								post content for the purpose inciting the
								hatred or dehumanisation of individuals or groups based on race, ethnic or national origin, religion, gender,
								gender identity, age, sexual orientation, disability or disease.
							</li>
							<li>
								<b>Harm to Minors.</b>
								{' '}
								While sexual violence occurs across the lifespan, for legal reasons, this platform is only
								to be used by individuals 18 years or older. We-Speak cannot accept stories from those aged under 18 years,
								or stories relating to Child Sexual Abuse. If you are under 18 and submit a story, or if your story relates to
								Child Sexual Abuse, we will be unable to publish it. If Stories submitted contain identifiable information about
								yourself and/or another person, then we have a duty to report any child protection concern to appropriate authorities,
								as per the Children First National Guidance for the Protection and Welfare of Children 2017. For more information,
								see our
								{' '}
								<LocaleLink title="Terms of Service" aria-label="Terms of Service" target="_blank" to="/legal/terms">Terms of Service</LocaleLink>
								.
							</li>
							<li>
								<b>Promotion or Glorification of Self-Harm.</b>
								{' '}
								While self-harm most certainly can co-occur with an individual’s
								survivorship experience,
								{' '}
								<span className="underline">do not</span>
								{' '}
								post content that actively promotes or glorifies
								self-harm. This includes content that urges or encourages others to: cut or injure themselves; embrace anorexia,
								bulimia, or other eating disorders; or attempt suicide rather than e.g., seeking counselling or treatment, or
								joining together in supportive conversation with those suffering or recovering from mental health conditions.
								Dialogue about these behaviours is incredibly important and online communities can be extraordinarily helpful to
								people struggling. If you do mention self-harm in your story, a content warning will be used to protect the
								safety of all survivors engaging with the platform.
							</li>
							<li>
								<b>Artwork.</b>
								{' '}
								In the case that a survivor submits a photography, realistic artwork, or other image depicting an
								easily identifiable person, we redact identifiable features (e.g. add a redaction blur to the face area). Even if
								the person’s face is not visible, it may still be necessary to redact other features or clothing of the person if
								they are particularly identifiable (e.g. unique costumes, name tags, etc.). The goal is to maintain the fidelity and
								purpose of the media while redacting identifiable components.
							</li>
							<li>
								<b>Adult Content.</b>
								{' '}
								While we give you the ability to upload visual media including artwork, illustrations, and
								images, be sure you are respecting the healing community we are trying to create by not featuring adult content in
								these images.
								{' '}
								<span className="underline">Do not</span>
								{' '}
								upload images that show real-life human genitals or depict sex
								acts. Certain types of artistic, educational, or political content featuring nudity are fine. The We-Speak team
								reserves the right to make posting decisions on all media uploaded to the platform.
							</li>
							<li>
								<b>Harassment.</b>
								{' '}
								<span className="underline">Do not</span>
								{' '}
								engage in targeted abuse, bullying, or harassment.
								{' '}
								<span className="underline">Do not</span>
								{' '}
								engage in the unwanted sexualization or sexual harassment of others. We
								have a zero tolerance policy for this.
							</li>
							<li>
								<b>Violent Content and Threats, Gore and Mutilation.</b>
								{' '}
								While this platform discusses violence, we
								{' '}
								<span className="underline">do not</span>
								{' '}
								condone threats or mutilation.
								{' '}
								<span className="underline">Do not</span>
								{' '}
								post
								content which includes violent threats toward individuals or groups. This includes threats of theft, property
								damage, or financial harm.
								{' '}
								<span className="underline">Do not</span>
								{' '}
								post violent content just to be shocking.
								{' '}
								<span className="underline">Do not</span>
								{' '}
								showcase the mutilation or torture of human beings, animals (including
								bestiality), or their remains.
								{' '}
								<span className="underline">Do not</span>
								{' '}
								post content that encourages or incites
								violence, or glorifies acts of violence or their perpetrators.
							</li>
							<li>
								<b>Stories That are Not Related to Trauma or Abuse.</b>
								{' '}
								The goal of We-Speak is to intentionally create a community
								for survivors of sexual violence. We are aware that many types of trauma and abuse can overlap with and be related
								to sexual trauma. Those stories are okay to post on the platform. We are also aware that not all stories related
								to survivorship have to do with the details of the harm experienced. Please
								{' '}
								<span className="underline">do not</span>
								{' '}
								feel that you need to disclose any details you are uncomfortable sharing. Stories that explicitly are not related
								to trauma or violence survivorship in any way, however, should be posted elsewhere.
							</li>
							<li>
								<b>Self-promotion.</b>
								{' '}
								To protect your privacy and confidentiality, We-Speak is an anonymous platform. This means
								that any information in story or media submissions that can be identifiable will be redacted before being posted to
								the platform. Unfortunately, this means that we are unable to link stories to personal blogs, social media pages, books
								you have authored, or other related initiatives. Stories with the central idea of promoting things you are working on
								(whether or not they are related to sexual violence survivorship) should be posted elsewhere. That being said, if
								you are doing something cool in this space and would like to partner with us or have any questions, reach out to
								either
								{' '}
								<LocaleLink title="info@we-consent.ie" aria-label="info@we-consent.ie" to="mailto:info@we-consent.ie?Subject=Request">info@we-consent.ie</LocaleLink>
								{' '}
								or our underlying technology partner
								{' '}
								<LocaleLink title="Our Wave" aria-label="Our Wave" target="_blank" to="https://www.ourwave.org" rel="noreferrer">Our Wave</LocaleLink>
								.
								We would love to hear from you!
							</li>
							<li>
								<b>Defamation.</b>
								{' '}
								The We-Speak platform is made possible by the anonymity of the stories submitted. This community
								can only grow and support it each other if all users do so in good faith and are conscious that any breach of anonymity,
								on another public platform of any kind, could jeopardise the existence of We-Speak into the future. By Irish law,
								people have a right to their good name. This right is protected by defamation law. Defamation refers to a situation
								where a comment, statement or publication negatively affects a person’s reputation which can be made online or
								offline. Contributors to the We-Speak website may also be active on social media. While it is very easy to publicise
								your own opinions and views on social media, please
								{' '}
								<span className="underline">do not</span>
								{' '}
								link what you upload to
								this site to your social media platforms. Think before you post, as you may be affecting somebody else’s reputation.
								This could in turn have a negative impact on your own life, the life of another and on the We-Speak platform. We
								trust that you will bear this in mind now and in the future, both on We-Speak and beyond, in order to empower
								this community to grow. We-Speak reserves the right to remove stories from the site, should we become aware that
								they are in breach of defamation law, or any of our other policies and guidelines.
							</li>
							<li>
								<b>Misattribution or Non-Attribution.</b>
								{' '}
								If you are posting the artwork or illustrations of others, make sure you
								always give proper credit or attribution. We’ve included a space for media uploads where you can include the artist’s
								name, social media handle, etc. This is important, as we want to credit the hard work artists put in to create the art
								that inspires us.
							</li>
						</ul>
						<br />
						<Typography tag="p">
							If we conclude that you are violating these guidelines, your story will not be posted on the We-Speak platform. We do our
							best to ensure fair outcomes, but in all cases we reserve the right to not post or remove content, for any reason, but
							particularly to protect survivors and the community we are trying to create. We reserve the right to enforce, or not
							enforce, these guidelines in our sole discretion, and these guidelines don&apos;t create a duty or contractual obligation
							for us to act in any particular manner. Repeat violations of our Community Guidelines may result in permanent suspension.
						</Typography>
						<Typography tag="p">
							You can report violations of these guidelines to us directly by emailing
							{' '}
							<LocaleLink title="info@we-consent.ie" aria-label="info@we-consent.ie" to="mailto:info@we-consent.ie?Subject=Request">info@we-consent.ie</LocaleLink>
							{' '}
							You can remove your own
							content whenever you want, of course, by using the link provided to you in your story posting notification email, or by
							requesting secure access to your story
							{' '}
							<LocaleLink title="Here" aria-label="Here" target="_blank" to="/find-story">here</LocaleLink>
							. If you
							have any questions or feedback, don’t hesitate to contact us using the email provided above.
						</Typography>
						<Typography tag="p">
							Thank you for reading all of this and for being interested in joining the We-Speak community. Welcome! We are so happy
							you are here. Your story matters and healing is possible.
						</Typography>
						<br />
						<Typography tag="p">
							A note on language: We know that language is important, and people feel differently about the terms used to describe
							people who have experienced sexual violence. Throughout the We-Speak platform, where space is limited, we will be
							using ‘survivor’, but we acknowledge that not everyone will identify with this term.
						</Typography>

						{/* Updated Date */}
						<br />
						<Typography tag="p">
							<i>Updated May 31, 2025</i>
						</Typography>

					</S.Wrapper>
				</LegalNavigation>
			</AppNavigation>
		</>
	);
};


/**
 * Configuration
 */

WeSpeakCommunityGuidelines.propTypes = {
	meta: PropTypes.shape()
};
WeSpeakCommunityGuidelines.defaultProps = {
	meta: {}
};


/**
 * Exports
 */

export default WeSpeakCommunityGuidelines;
